class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.message = this.querySelector('[data-cart-notification-message]');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.pauseTimer = this.pauseTimer.bind(this);
    this.resumeTimer = this.resumeTimer.bind(this);

    // Toast mode (cart drawer + "toast" add behaviour): non-blocking status
    // notice that auto-dismisses and whose "View cart" opens the drawer.
    this.toastMode = this.dataset.mode === 'toast';
    this.duration = parseInt(this.dataset.duration, 10) || 6000;
    this.timer = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('.cart-notification__close').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.querySelectorAll('[data-open-cart-drawer]').forEach((button) =>
      button.addEventListener('click', () => this.openCartDrawer(button))
    );

    if (this.toastMode) {
      this.notification.addEventListener('mouseenter', this.pauseTimer);
      this.notification.addEventListener('mouseleave', this.resumeTimer);
      this.notification.addEventListener('focusin', this.pauseTimer);
      this.notification.addEventListener('focusout', this.resumeTimer);
    }
  }

  get isOpen() {
    return this.notification.classList.contains('active');
  }

  open() {
    this.notification.classList.add('animate', 'active');

    if (this.toastMode) {
      // Keep the shopper where they are: no focus move, no trap.
      this.startTimer();
    } else {
      this.notification.addEventListener(
        'transitionend',
        () => {
          this.notification.focus();
          trapFocus(this.notification);
        },
        { once: true }
      );
    }

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.clearTimer();
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    if (!this.toastMode) removeTrapFocus(this.activeElement);
  }

  openCartDrawer(button) {
    const drawer = document.querySelector('cart-drawer');
    this.close();
    if (drawer && typeof drawer.open === 'function') drawer.open(button);
  }

  startTimer() {
    this.clearTimer();
    this.timer = setTimeout(() => this.close(), this.duration);
  }

  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  pauseTimer() {
    this.clearTimer();
  }

  resumeTimer(event) {
    if (event && event.type === 'focusout' && this.notification.contains(event.relatedTarget)) return;
    if (this.isOpen) this.startTimer();
  }

  renderContents(parsedState) {
    this.updateMessage(parsedState);

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionHtml) return;

      const target = document.getElementById(section.id);
      if (!target) return;

      target.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  updateMessage(parsedState) {
    if (!this.message) return;

    const itemsAdded = parsedState.itemsAdded || parsedState.items?.length || parsedState.quantity || 1;
    const count = Number.parseInt(itemsAdded, 10);

    if (count > 1 && this.dataset.messageMultiple) {
      this.message.textContent = this.dataset.messageMultiple.replace('__COUNT__', count);
      if (!this.toastMode) this.notification.setAttribute('aria-label', this.message.textContent);
      return;
    }

    this.message.textContent = this.dataset.messageSingle || this.message.textContent;
    if (!this.toastMode) this.notification.setAttribute('aria-label', this.message.textContent);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
